import { cloneHomefront } from '../../../rules/subplotGrid'
import { hexDistance } from '../../../rules/hexGrid'
import { applyCraterDamage, craterFootprint, nearestStructureIntel } from '../../../rules/subplotDamage'
import { generateHomefrontV3 } from '../../../rules/homefrontAutogen'
import { seededRng } from '../../../rules/bots/rng'

// ─── ?preview&peek — a scripted intel Hive for iterating on the eye peek ──────
// Generates a fresh enemy Hive the way a war does (autogen, today's structure
// shapes; the archived fixture's are older square-era shapes), seeded so it's
// the same every load, then fires a fixed set of real craters (applyCraterDamage, so
// stamps, letters and dead-marking are the rules' own):
//   A  one bearing, never found            → one ring
//   B  three bearings, near 4, 3 and 6 away  → three rings, played in turn
//   C  one bearing, then struck (alive)    → labelled C; its stamp hides
//   D  one bearing, then killed            → labelled D, dimmed (the kill
//                                            crater's own bearing names the
//                                            next survivor, a fourth B ring)
// Each bearing's crater is a margin-1 blast that touches no structure, aimed
// as far as possible from that letter's earlier aims so the rings spread out.
// A structure the script can't use (too few live ones) is just skipped.

const BEARING_MARGIN = 1

function wipeIntel(hf) {
    const h = cloneHomefront(hf)
    for (const cell of h.subplots) {
        if (!cell) continue
        Object.assign(cell, {
            hp: cell.maxHp ?? 1, destroyed: false, revealed: false, scanned: false,
            distance: null, distanceLetter: null, distanceDead: false, impact: false,
        })
    }
    h.intelLetters = {}
    return h
}

const structureCells = (hf) => new Set(
    Object.values(hf.structures ?? {}).flatMap(st => st.footprint ?? [])
)

// A clean aim whose bearing names `id`, at the distance closest to `want`.
function bearingAim(hf, id, want, avoid) {
    const taken = structureCells(hf)
    let best = null
    hf.subplots.forEach((cell, s) => {
        if (!cell || cell.destroyed) return
        const fp = craterFootprint(s, BEARING_MARGIN)
        if (fp.some(f => taken.has(f) || hf.subplots[f]?.destroyed)) return
        const { distance, structureId } = nearestStructureIntel(hf, s)
        if (structureId !== id) return
        const spread = avoid.length ? Math.min(...avoid.map(a => hexDistance(a, s))) : 0
        const score = Math.abs(distance - want) * 100 - spread
        if (!best || score < best.score) best = { s, score }
    })
    return best?.s ?? null
}

// Strike C without killing it: an aim off its footprint whose blast takes
// exactly one of its cells and touches no other structure.
function grazeAim(hf, id) {
    const own = new Set(hf.structures[id].footprint)
    const others = structureCells(hf)
    own.forEach(s => others.delete(s))
    const s = hf.subplots.findIndex((cell, s) => {
        if (!cell || own.has(s)) return false
        const fp = craterFootprint(s, BEARING_MARGIN)
        return fp.filter(f => own.has(f)).length === 1 && !fp.some(f => others.has(f))
    })
    return s < 0 ? null : s
}

function bearing(hf, id, want, avoid = []) {
    const s = bearingAim(hf, id, want, avoid)
    if (s == null) return hf
    avoid.push(s)
    return applyCraterDamage(hf, s, BEARING_MARGIN).homefront
}

export const PEEK_DEMO_SEED = 6     // B lands bearings at 4, 3 and 6

export function buildPeekDemoHive(seed = PEEK_DEMO_SEED) {
    let hf = wipeIntel(generateHomefrontV3(seededRng(seed)))
    const live = Object.values(hf.structures ?? {})
        .filter(st => (st.footprint?.length ?? 0) >= 2)
        .map(st => st.id)
    const [a, b, c, d] = live
    if (a) hf = bearing(hf, a, 5)
    if (b) {
        const avoid = []
        for (const want of [4, 3, 6]) hf = bearing(hf, b, want, avoid)
    }
    if (c) {
        hf = bearing(hf, c, 3)
        const s = grazeAim(hf, c)
        if (s != null) hf = applyCraterDamage(hf, s, BEARING_MARGIN).homefront
    }
    if (d) {
        hf = bearing(hf, d, 2)
        // Kill D: a big blast centred on its middle cell.
        const fp = hf.structures[d].footprint
        hf = applyCraterDamage(hf, fp[Math.floor(fp.length / 2)], 3).homefront
    }
    return hf
}
